import store from '@/store/store';
import Utils from '@/helpers/Utils';

const urlHost = document?.location?.host || '';
const getBaseUrlByHost = () => {
    if (urlHost === 'broker.tekta.ru') return 'https://broker.tekta.ru/';
    if (urlHost === 'broker-stage.tekta.ru') return 'https://broker-stage.tekta.ru/';
    return 'https://broker-dev.tekta.ru/';
};

export const baseURL = urlHost.includes('localhost') ? 'http://localhost:80' : getBaseUrlByHost();
export const csrfCookie = '/csrf-cookie';

export const AUTH_CHECK_FAILED = 'AUTH_CHECK_FAILED';
export const LOGIN_PATH = '/login';

const ACCESS_EXPIRES_KEY = 'tekta_token_expires_on';
const REFRESH_EXPIRES_KEY = 'expiresInRefresh';
const ACCESS_COOKIE = 'tekta_access_token';
const AUTH_PREFIX = '/auth/';
const LOGOUT_PATH = '/auth/logout';
const REFRESH_PATH = '/auth/token/refresh';
const MOCK_PREFIX = '/tests/';

const PUBLIC_EXACT_PATHS = new Set([
    csrfCookie,
    '/api/payment/check',
    '/api/settings/getAuth',
    '/api/settings/getPopup',
    '/api/agreementText',
    '/api/broker/agreementText',
    '/api/form/support',
    '/api/v1/encryptPassword'
]);

const PUBLIC_PREFIXES = [
    '/api/questions/'
];

export const createAuthCheckError = () => {
    const error = new Error('Запрос отменён: нет авторизации');

    error.code = AUTH_CHECK_FAILED;

    return error;
};

export const normalizeRequestPath = (url = '') => {
    const value = String(url || '').split('?')[0];

    if (/^https?:\/\//i.test(value)) {
        try {
            return new URL(value).pathname || '';
        } catch (error) {
            return value;
        }
    }

    return value;
};

export const isLogoutRequest = (url) => normalizeRequestPath(url) === LOGOUT_PATH;

export const needsCredentials = (url) => {
    const path = normalizeRequestPath(url);

    if (path === LOGOUT_PATH) {
        return false;
    }

    return path === csrfCookie || path === REFRESH_PATH || path.startsWith(AUTH_PREFIX);
};

export const getAccessToken = () => store.state.token || Utils.getCookie(ACCESS_COOKIE) || '';

export const isPublicRequest = (url) => {
    const path = normalizeRequestPath(url);

    if (path.startsWith(AUTH_PREFIX) || path.startsWith(MOCK_PREFIX)) {
        return true;
    }

    if (PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix))) {
        return true;
    }

    return PUBLIC_EXACT_PATHS.has(path);
};

export const hasActiveToken = () => Boolean(getAccessToken());

const readExpiresAt = (key) => {
    const raw = localStorage.getItem(key);

    if (raw == null || raw === '') {
        return 0;
    }

    const value = Number(raw);

    return Number.isFinite(value) ? value : 0;
};

let refreshInFlight = null;
let exitInFlight = null;
let loginRedirectScheduled = false;

export const isLoginRedirectScheduled = () => loginRedirectScheduled;

const refreshTokensOnce = () => {
    if (!refreshInFlight) {
        refreshInFlight = store.dispatch('refreshAllTokens').finally(() => {
            refreshInFlight = null;
        });
    }

    return refreshInFlight;
};

const redirectToLogin = () => {
    const path = window.location.pathname;

    if (path === LOGIN_PATH || loginRedirectScheduled) {
        return;
    }

    loginRedirectScheduled = true;

    const params = new URLSearchParams(window.location.search);

    params.delete('redirect');

    const rest = params.toString();
    const redirect = `redirect=${encodeURIComponent(path)}`;
    const query = rest ? `?${rest}&${redirect}` : `?${redirect}`;

    window.location.replace(`${LOGIN_PATH}${query}`);
};

const runExit = async () => {
    try {
        await store.dispatch('logout');
    } catch (error) {
        // Ошибка /auth/logout не отменяет редирект.
    } finally {
        redirectToLogin();
    }
};

export const goOut = () => {
    if (!exitInFlight) {
        exitInFlight = runExit().finally(() => {
            exitInFlight = null;
        });
    }

    return exitInFlight;
};

// true — запрос можно отправлять, false — сессии нет или она сброшена.
export const checkAuthCrmUser = async () => {
    try {
        const expiresOn = readExpiresAt(ACCESS_EXPIRES_KEY);

        if (!expiresOn) {
            if (hasActiveToken()) {
                await goOut();
            }

            return false;
        }

        if (Date.now() < expiresOn) {
            return true;
        }

        const expiresInRefresh = readExpiresAt(REFRESH_EXPIRES_KEY);

        if (!expiresInRefresh || Date.now() >= expiresInRefresh) {
            await goOut();

            return false;
        }

        await refreshTokensOnce();

        return true;
    } catch (error) {
        try {
            await goOut();
        } catch (exitError) {
            // Редирект не должен превращать проверку в отклонённый промис.
        }

        return false;
    }
};
